/**
 * BoneAttachmentSystem
 * 
 * Utility class for bone-related operations on characters.
 * Extracted from CombatSystem for single-responsibility.
 */

import { Vector3, Bone, AbstractMesh } from '@babylonjs/core';
import type { Character } from './Character';
import type { CharacterAsset } from '../assets/CharacterLoader';

/**
 * Get bone world position for a character.
 * 
 * @param character - The character to get bone position from
 * @param boneName - The name of the bone
 * @returns World position of the bone, or null if not found
 */
export function getBoneWorldPosition(character: Character, boneName: string): Vector3 | null {
  const characterAsset = (character as any).characterAsset as CharacterAsset | undefined;
  const skeleton = characterAsset?.skeleton;
  const rootMesh: AbstractMesh | undefined = characterAsset?.rootMesh;
  if (!skeleton) return null;
  
  const bone = skeleton.bones.find((b: Bone) => b.name === boneName);
  if (!bone) return null;
  
  // Get bone position in world space by using the linked transform node
  // bone.getAbsolutePosition() returns skeleton-local, we need world space
  const transform = bone.getTransformNode();
  if (transform) {
    return transform.getAbsolutePosition().clone();
  }
  
  // Fallback: manually transform bone position by mesh world matrix
  if (rootMesh) {
    const boneLocalPos = bone.getAbsolutePosition();
    return Vector3.TransformCoordinates(boneLocalPos, rootMesh.getWorldMatrix());
  }
  
  return bone.getAbsolutePosition().clone();
}

/**
 * Get bone world forward direction for a character.
 * 
 * @param character - The character to get bone direction from
 * @param boneName - The name of the bone
 * @returns Normalized forward direction of the bone, or null if not found
 */
export function getBoneWorldForward(character: Character, boneName: string): Vector3 | null {
  const characterAsset = (character as any).characterAsset as CharacterAsset | undefined;
  const skeleton = characterAsset?.skeleton;
  if (!skeleton) return null;
  
  const bone = skeleton.bones.find((b: Bone) => b.name === boneName);
  if (!bone) return null;
  
  const transform = bone.getTransformNode();
  if (!transform) return null;
  
  return transform.getDirection(Vector3.Forward()).normalize();
}

/**
 * Get bone world up direction for a character.
 * 
 * @param character - The character to get bone direction from
 * @param boneName - The name of the bone
 * @returns Normalized up direction of the bone, or null if not found
 */
export function getBoneWorldUp(character: Character, boneName: string): Vector3 | null {
  const characterAsset = (character as any).characterAsset as CharacterAsset | undefined;
  const skeleton = characterAsset?.skeleton;
  if (!skeleton) return null;
  
  const bone = skeleton.bones.find((b: Bone) => b.name === boneName);
  if (!bone) return null;
  
  const transform = bone.getTransformNode();
  if (!transform) return null;
  
  return transform.getDirection(Vector3.Up()).normalize();
}

/**
 * Find a bone by name in a character's skeleton.
 * 
 * @param character - The character to search
 * @param boneName - The name of the bone
 * @returns The bone, or null if not found
 * @throws Error if character has no skeleton (fail-fast)
 */
export function findBone(character: Character, boneName: string): Bone | null {
  const characterAsset = (character as any).characterAsset as CharacterAsset | undefined;
  const skeleton = characterAsset?.skeleton;
  
  if (!skeleton) {
    throw new Error(`BoneAttachmentSystem: Character ${character.getId()} has no skeleton`);
  }
  
  return skeleton.bones.find((b: Bone) => b.name === boneName) ?? null;
}

/**
 * Find a bone by name, throwing if not found (fail-fast).
 * 
 * @param character - The character to search
 * @param boneName - The name of the bone
 * @returns The bone
 * @throws Error if bone not found
 */
export function requireBone(character: Character, boneName: string): Bone {
  const bone = findBone(character, boneName);
  if (!bone) {
    throw new Error(`BoneAttachmentSystem: Bone '${boneName}' not found in character ${character.getId()}`);
  }
  return bone;
}
